import { useMemo } from "react";
import { GroupBlockData, useMultipleGroupBlocks } from "../../configMap";
import {
  CategorizationColumnItem,
  EntityColumnItem,
  GroupBlockTable,
  GroupBlockTableData,
} from "../interfaces/groupBlockTable";
import { getInLanguageOrDefault } from "../../common/utilities/language";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_CATEGORIZATION_WIDTH,
  DEFAULT_ENTITY_WIDTH,
} from "../constants";
import { useGroupBlockCategorizations } from "../../configMap/hooks/useGroupBlockCategorizations";
import { useAppSelector } from "../../app";
import annotationsForGroupBlockSelector from "../selectors/annotationsForGroupBlockSelector";
import { shallowEqual } from "react-redux";

const useGroupBlockForTable = (
  selectedGroupBlock: GroupBlockData | undefined,
  disabled: boolean,
) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { countPerGroupBlockId } = useMultipleGroupBlocks();
  const { categorizationLinks } = useGroupBlockCategorizations(
    selectedGroupBlock?.id,
  );

  const categorizationColumnItems: Array<CategorizationColumnItem> =
    useMemo(() => {
      if (!selectedGroupBlock) {
        return [];
      }

      const categorizationsSorted = [...categorizationLinks].sort(
        (a, b) =>
          (a.categorization.index ?? Number.MAX_VALUE) -
          (b.categorization.index ?? Number.MAX_VALUE),
      );

      return categorizationsSorted.map((categorizationWithConfig) => {
        const name = getInLanguageOrDefault(
          categorizationWithConfig.categorization.label,
          language,
        );
        const widthInPx =
          categorizationWithConfig.categorization.widthInPx ??
          DEFAULT_CATEGORIZATION_WIDTH;

        return {
          title: `${name} ${categorizationWithConfig.required ? "*" : ""}`,
          key: categorizationWithConfig.id,
          width: `${widthInPx}px`,
          disabled: disabled,
          categorizationWithConfig: categorizationWithConfig,
          groupBlockId: selectedGroupBlock.id,
          categorizationCellKey: categorizationWithConfig.categorization.id,
        };
      });
    }, [language, disabled, selectedGroupBlock, categorizationLinks]);

  const entityColumnItems: Array<EntityColumnItem> = useMemo(() => {
    if (!selectedGroupBlock) {
      return [];
    }

    const entityTypesSorted = selectedGroupBlock.groupBlockEntityTypes
      ? [...selectedGroupBlock.groupBlockEntityTypes].sort(
          (a, b) => a.index - b.index,
        )
      : [];

    return entityTypesSorted.map((groupBlockEntityType) => {
      const name = getInLanguageOrDefault(
        groupBlockEntityType.entityType.translations,
        language,
      );
      const widthInPx = groupBlockEntityType.widthInPx ?? DEFAULT_ENTITY_WIDTH;

      return {
        title: `${name} ${groupBlockEntityType.required ? "*" : ""}`,
        key: groupBlockEntityType.entityType.id,
        width: `${widthInPx}px`,
        disabled: disabled,
        groupBlockEntityType: groupBlockEntityType,
      };
    });
  }, [language, disabled, selectedGroupBlock]);

  const annotations = useAppSelector(
    (state) =>
      annotationsForGroupBlockSelector(state, {
        groupBlockId: selectedGroupBlock?.id,
      }),
    shallowEqual,
  );

  const data: Array<GroupBlockTableData> = useMemo(() => {
    if (!selectedGroupBlock) {
      return [];
    }

    const count = countPerGroupBlockId(selectedGroupBlock.id);

    // Create base rows
    const baseRows: Array<GroupBlockTableData> = Array(count)
      .fill(0)
      .map((_, index) => {
        return {
          index: index + 1,
          groupBlock: selectedGroupBlock,
          count,
          isLoading: false,
        };
      });

    // Determine page for each row by checking annotations
    const rowPages = new Map<number, number>();

    annotations?.forEach((annotation) => {
      // Only placed annotations (with actual token positions) carry a
      // meaningful page. Unplaced placeholders default to page 0 and would
      // otherwise corrupt the page mapping, producing spurious page breaks.
      const isPlaced =
        annotation.pageTokenIndices !== null &&
        annotation.pageTokenIndices !== undefined &&
        annotation.pageTokenIndices.length > 0;

      if (annotation.index !== undefined && isPlaced) {
        rowPages.set(annotation.index, annotation.page);
      }
    });

    // Insert page break rows
    const rowsWithPageBreaks: Array<GroupBlockTableData> = [];
    let lastPage: number | undefined;

    baseRows.forEach((row) => {
      const currentPage = rowPages.get(row.index);

      if (
        currentPage !== undefined &&
        lastPage !== undefined &&
        currentPage !== lastPage
      ) {
        rowsWithPageBreaks.push({
          index: row.index - 0.5, // Use fractional index for page breaks
          groupBlock: selectedGroupBlock,
          count,
          isLoading: false,
          isPageBreak: true,
          pageNumber: currentPage,
        });
      }

      rowsWithPageBreaks.push(row);

      if (currentPage !== undefined) {
        lastPage = currentPage;
      }
    });

    return rowsWithPageBreaks;
  }, [countPerGroupBlockId, selectedGroupBlock, annotations]);

  const selectedGroupBlockForTable: GroupBlockTable | undefined =
    useMemo(() => {
      if (!selectedGroupBlock) {
        return undefined;
      }

      return {
        groupBlock: selectedGroupBlock,
        entityColumnItems,
        categorizationColumnItems,
        data,
      };
    }, [
      selectedGroupBlock,
      categorizationColumnItems,
      entityColumnItems,
      data,
    ]);

  return selectedGroupBlockForTable;
};

export default useGroupBlockForTable;
